/**
 * The "you have credit" reminder: a ribbon at the top of the shop, and a
 * progress card on the upload page while a minimum is not reached yet.
 *
 * Both only INFORM. Credit goes on automatically at checkout once the order
 * qualifies, so there is no apply button here and nothing here applies
 * anything. The figures are the server's: the ribbon reads the customer's
 * live lots (GET /credit/mine signed in, GET /credit/balance with a claim
 * token), the progress card reads `lockedByMinimum` / `orderGoodsCents`
 * through `creditMinimumLines`. Nothing here re-derives which credit an
 * order may use; it only picks the shop's own lots and sums them.
 *
 * Pure, so it is tested; `CreditReminderRibbon` and `CreditMinimumProgress`
 * only render it.
 */
import type { CreditMinimumLine } from "./credit-minimum";
import type { CreditLotView } from "./credit-lots";
import { decodeCreditToken } from "./credit-token";

export type Message = { key: string; fallback: string; params: Record<string, string> };

/** One lot as the ribbon needs it, from either endpoint. */
export type ReminderLot = {
  amountCents: number;
  minimumOrderCents: number;
  expiresAt: string | null;
  currency: string;
  market: string | null;
};

/**
 * The credit ledger's currency for a shop currency. The ledger keeps one EUR
 * (the backend's `normalizeCreditCurrency`): the Austrian shop's "eur-at" is
 * a price list, not a currency of its own.
 */
export function creditCurrencyOf(shopCurrency: string | null | undefined): string {
  const lower = (shopCurrency ?? "").trim().toLowerCase();
  return lower === "eur-at" ? "eur" : lower;
}

/** GET /credit/mine's lots. */
export function reminderLotsFromMine(
  lots:
    | ReadonlyArray<{
        currency: string;
        remainingCents: number;
        expiresAt: string | Date | null;
        minimumOrderCents: number;
        market: string | null;
      }>
    | null
    | undefined,
): ReminderLot[] {
  return (lots ?? []).map((l) => ({
    amountCents: l.remainingCents,
    minimumOrderCents: l.minimumOrderCents,
    expiresAt: l.expiresAt ? String(l.expiresAt) : null,
    currency: l.currency,
    market: l.market,
  }));
}

/**
 * GET /credit/balance's lots. A lot whose whole amount is on another cart
 * that is being paid (HELD_ON_OTHER_CART) is not the customer's to spend
 * again, so it is left out.
 */
export function reminderLotsFromBalance(
  lots: ReadonlyArray<CreditLotView> | null | undefined,
): ReminderLot[] {
  return (lots ?? [])
    .filter((l) => l.reason !== "HELD_ON_OTHER_CART")
    .map((l) => ({
      amountCents: l.availableCents,
      minimumOrderCents: l.minimumOrderCents,
      expiresAt: l.expiresAt,
      currency: l.currency,
      market: l.market,
    }));
}

export type CreditRibbonView = {
  totalCents: number;
  /** The part with no minimum: it comes off any order. */
  freeCents: number;
  /** The part held back by a minimum (`minimumOrderCents`). */
  lockedCents: number;
  currency: string;
  /** The lowest minimum among the lots that have one; null when none does. */
  minimumOrderCents: number | null;
  /** The soonest expiry; null when no lot expires. */
  expiresAt: string | null;
};

/**
 * What the ribbon says for THIS shop, or null for nothing at all.
 *
 * Only the lots this shop can take: its currency, and a gift card's market
 * when it has one (a .hr card is no use on .si). Several lots read as one:
 * the total, the soonest expiry, and the lowest minimum among the lots that
 * have one — with the part that has no minimum kept apart, so a mix does not
 * read as if all of it waited for the minimum.
 */
export function creditRibbonView(input: {
  lots: readonly ReminderLot[];
  shopCurrency: string | null | undefined;
  market: string | null | undefined;
}): CreditRibbonView | null {
  const currency = creditCurrencyOf(input.shopCurrency);
  if (!currency) return null;
  const mine = input.lots.filter(
    (l) =>
      l.amountCents > 0 &&
      creditCurrencyOf(l.currency) === currency &&
      (!l.market || !input.market || l.market === input.market),
  );
  if (mine.length === 0) return null;
  const minimums = mine.map((l) => l.minimumOrderCents).filter((m) => m > 0);
  const expiries = mine
    .map((l) => l.expiresAt)
    .filter((e): e is string => !!e && !Number.isNaN(new Date(e).getTime()))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  const lockedCents = mine
    .filter((l) => l.minimumOrderCents > 0)
    .reduce((acc, l) => acc + l.amountCents, 0);
  const totalCents = mine.reduce((acc, l) => acc + l.amountCents, 0);
  return {
    totalCents,
    freeCents: totalCents - lockedCents,
    lockedCents,
    currency,
    minimumOrderCents: minimums.length ? Math.min(...minimums) : null,
    expiresAt: expiries[0] ?? null,
  };
}

/**
 * The ribbon's two parts: the bold amount, and the condition after the dot.
 * An empty `date` (no expiry) leaves the date out.
 *
 * A mix of credit without and with a minimum names both parts: stating only
 * the minimum would tell the customer the free part waits for it too.
 */
export function creditRibbonMessages(
  view: CreditRibbonView,
  money: (cents: number) => string,
  date: string,
): { amount: Message; detail: Message } {
  const amount: Message = {
    key: "creditReminder.ribbonAmount",
    fallback: "Na računu imate {{amount}} dobroimetja",
    params: { amount: money(view.totalCents) },
  };
  if (view.minimumOrderCents !== null && view.freeCents > 0) {
    const params = {
      free: money(view.freeCents),
      locked: money(view.lockedCents),
      minimum: money(view.minimumOrderCents),
    };
    return {
      amount,
      detail: date
        ? {
            key: "creditReminder.ribbonMixedUntil",
            fallback:
              "{{free}} se odšteje pri vsakem naročilu, še {{locked}} pri naročilu od {{minimum}} naprej, do {{date}}",
            params: { ...params, date },
          }
        : {
            key: "creditReminder.ribbonMixed",
            fallback:
              "{{free}} se odšteje pri vsakem naročilu, še {{locked}} pri naročilu od {{minimum}} naprej",
            params,
          },
    };
  }
  if (view.minimumOrderCents !== null) {
    const minimum = money(view.minimumOrderCents);
    return {
      amount,
      detail: date
        ? {
            key: "creditReminder.ribbonMinimumUntil",
            fallback: "za naročila od {{minimum}} naprej, do {{date}}",
            params: { minimum, date },
          }
        : {
            key: "creditReminder.ribbonMinimum",
            fallback: "za naročila od {{minimum}} naprej",
            params: { minimum },
          },
    };
  }
  return {
    amount,
    detail: date
      ? {
          key: "creditReminder.ribbonAutoUntil",
          fallback: "ob naslednjem naročilu se odšteje samo, do {{date}}",
          params: { date },
        }
      : {
          key: "creditReminder.ribbonAuto",
          fallback: "ob naslednjem naročilu se odšteje samo",
          params: {},
        },
  };
}

/**
 * Where the ribbon may show. `pathname` is the browser path, with or without
 * the locale segment (`/sl/order/…` and `/order/…` read the same).
 *
 *  - never inside the mobile app's WebView: the app has its own chrome;
 *  - never on admin pages;
 *  - never on the checkout (`/order/<id>/…`), which shows the credit itself,
 *    nor on the pages after it;
 *  - never on "Moja naročila", whose credit card already says all of it.
 */
export function creditRibbonAllowed(input: {
  pathname: string | null | undefined;
  embedded: boolean;
}): boolean {
  if (input.embedded) return false;
  const path = (input.pathname ?? "/").replace(/^\/[a-z]{2}(?:-[A-Za-z]{2})?(?=\/|$)/, "") || "/";
  return ![
    /^\/admin(\/|$)/,
    /^\/order(\/|$)/,
    /^\/success(\/|$)/,
    /^\/cancel(\/|$)/,
    /^\/render(\/|$)/,
  ].some((re) => re.test(path));
}

/**
 * Whether a held claim token may be shown on the ribbon for a customer who is
 * not signed in. Stricter would hide it from the guest who just clicked their
 * email link and has no order email yet; looser would show a previous guest's
 * credit to whoever is on the browser next. So: a token that names an
 * address, and an order that, if it already has an email, has the same one.
 */
export function ribbonTokenUsable(
  token: string | null,
  orderEmail: string | null | undefined,
): boolean {
  if (!token) return false;
  const email = decodeCreditToken(token)?.email ?? "";
  if (!email) return false;
  const order = (orderEmail ?? "").trim().toLowerCase();
  return !order || order === email;
}

export type CreditProgressView =
  | { kind: "hidden" }
  | {
      kind: "short";
      amountCents: number;
      minimumOrderCents: number;
      missingCents: number;
      goodsCents: number;
      /** 0–100, goods over minimum. */
      percent: number;
    }
  | { kind: "reached"; amountCents: number; minimumOrderCents: number };

/**
 * The upload page's progress card.
 *
 *  - "short": the next minimum this order does not reach yet (the lowest:
 *    the server lists `lockedByMinimum` lowest first), from the server's own
 *    goods total, which leaves shipping out;
 *  - "reached": every minimum is met and credit with a minimum will come off
 *    at checkout — the lots the server judged applicable that carry one;
 *  - "hidden": no credit with a minimum, or the server did not say enough
 *    (no goods total, no lots) to draw a bar honestly.
 */
export function creditProgressView(input: {
  lines: readonly CreditMinimumLine[];
  lots: readonly CreditLotView[] | null;
}): CreditProgressView {
  const short = input.lines.find((l) => l.missingCents !== null && l.missingCents > 0);
  if (short && short.missingCents !== null && short.minimumOrderCents > 0) {
    const goodsCents = Math.max(0, short.minimumOrderCents - short.missingCents);
    return {
      kind: "short",
      amountCents: short.amountCents,
      minimumOrderCents: short.minimumOrderCents,
      missingCents: short.missingCents,
      goodsCents,
      percent: Math.min(100, Math.max(0, (goodsCents / short.minimumOrderCents) * 100)),
    };
  }
  if (input.lines.length > 0) return { kind: "hidden" };
  const reached = (input.lots ?? []).filter(
    (l) => l.applicable && l.minimumOrderCents > 0 && l.availableCents > 0,
  );
  if (reached.length === 0) return { kind: "hidden" };
  return {
    kind: "reached",
    amountCents: reached.reduce((acc, l) => acc + l.availableCents, 0),
    minimumOrderCents: Math.max(...reached.map((l) => l.minimumOrderCents)),
  };
}

/** The card's sentence for a view that shows. */
export function creditProgressMessage(
  view: Exclude<CreditProgressView, { kind: "hidden" }>,
  money: (cents: number) => string,
): Message {
  if (view.kind === "short") {
    return {
      key: "creditReminder.progressMissing",
      fallback: "Dodajte še za {{missing}} in porabite {{amount}} dobroimetja",
      params: { missing: money(view.missingCents), amount: money(view.amountCents) },
    };
  }
  return {
    key: "creditReminder.progressReached",
    fallback: "Od {{minimum}} naprej se {{amount}} dobroimetja odšteje ob zaključku.",
    params: { minimum: money(view.minimumOrderCents), amount: money(view.amountCents) },
  };
}
